import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import styles from './WidgetMonthPicker.module.scss';

type Props = {
	dateFrom: Date;
	dateTo: Date;
	onChange: (from: Date, to: Date) => void;
};

type Step = 'from' | 'to';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DROPDOWN_WIDTH = 220;
const DROPDOWN_MARGIN = 6;

const formatMonth = (date: Date) => dayjs(date).format('MMM YYYY');

const WidgetMonthPicker = ({ dateFrom, dateTo, onChange }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [step, setStep] = useState<Step>('from');
	const [year, setYear] = useState(() => dayjs(dateFrom).year());
	const [tempFrom, setTempFrom] = useState<Date>(dateFrom);
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
	const pillRef = useRef<HTMLButtonElement>(null);

	const computePosition = () => {
		const el = pillRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		let left = rect.right - DROPDOWN_WIDTH;
		if (left < 8) left = 8;
		if (left + DROPDOWN_WIDTH > viewportWidth - 8) left = viewportWidth - DROPDOWN_WIDTH - 8;
		setDropdownPos({ top: rect.bottom + DROPDOWN_MARGIN, left });
	};

	useLayoutEffect(() => {
		if (isOpen) computePosition();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const onScrollOrResize = () => computePosition();
		window.addEventListener('resize', onScrollOrResize);
		window.addEventListener('scroll', onScrollOrResize, true);
		return () => {
			window.removeEventListener('resize', onScrollOrResize);
			window.removeEventListener('scroll', onScrollOrResize, true);
		};
	}, [isOpen]);

	const handleOpen = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isOpen) {
			setIsOpen(false);
			return;
		}
		setStep('from');
		setYear(dayjs(dateFrom).year());
		setTempFrom(dateFrom);
		setIsOpen(true);
	};

	const handleMonthClick = (monthIndex: number) => {
		if (step === 'from') {
			const from = dayjs().year(year).month(monthIndex).startOf('month').toDate();
			setTempFrom(from);
			setStep('to');
		} else {
			let from = tempFrom;
			let to = dayjs().year(year).month(monthIndex).endOf('month').toDate();

			if (to < from) {
				const swap = from;
				from = dayjs().year(year).month(monthIndex).startOf('month').toDate();
				to = dayjs(swap).endOf('month').toDate();
			}

			onChange(from, to);
			setIsOpen(false);
		}
	};

	const isFromSelected = (monthIndex: number) => {
		const d = dayjs().year(year).month(monthIndex);
		if (step === 'to') return d.isSame(dayjs(tempFrom), 'month');
		return d.isSame(dayjs(dateFrom), 'month');
	};

	const isToSelected = (monthIndex: number) => {
		if (step === 'to') return false;
		const d = dayjs().year(year).month(monthIndex);
		return d.isSame(dayjs(dateTo), 'month');
	};

	const isInRange = (monthIndex: number) => {
		const d = dayjs().year(year).month(monthIndex);
		if (step === 'to') {
			// During "to" selection, highlight from tempFrom onwards
			return d.isAfter(dayjs(tempFrom), 'month');
		}
		// When showing current selection, highlight between dateFrom and dateTo
		const from = dayjs(dateFrom);
		const to = dayjs(dateTo);
		return d.isAfter(from, 'month') && d.isBefore(to, 'month');
	};

	return (
		<div className={styles.container}>
			<button ref={pillRef} className={styles.pill} onClick={handleOpen} type="button">
				<span>{formatMonth(dateFrom)} - {formatMonth(dateTo)}</span>
			</button>
			{isOpen && dropdownPos && createPortal(
				<>
					<div className={styles.backdrop} onClick={() => setIsOpen(false)} />
					<div
						className={styles.dropdown}
						style={{ top: dropdownPos.top, left: dropdownPos.left }}
					>
						<div className={styles.stepLabel}>
							{step === 'from' ? 'De' : 'Até'}
						</div>
						<div className={styles.yearRow}>
							<button className={styles.yearArrow} onClick={() => setYear(y => y - 1)} type="button">‹</button>
							<span className={styles.yearText}>{year}</span>
							<button className={styles.yearArrow} onClick={() => setYear(y => y + 1)} type="button">›</button>
						</div>
						<div className={styles.monthGrid}>
							{MONTHS.map((name, i) => (
								<button
									key={i}
									type="button"
									className={`${styles.monthCell} ${isFromSelected(i) || isToSelected(i) ? styles.monthSelected : ''} ${isInRange(i) ? styles.monthInRange : ''}`}
									onClick={() => handleMonthClick(i)}
								>
									{name}
								</button>
							))}
						</div>
					</div>
				</>,
				document.body
			)}
		</div>
	);
};

export default WidgetMonthPicker;
